import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Database, 
  HardDrive,
  Download,
  Upload,
  Trash2,
  Clock,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Activity,
  BarChart3,
  Calendar,
  FileArchive,
  Shield
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import databaseService, { DatabaseStats, DatabaseHealth, BackupInfo } from '@/services/databaseService';

const DatabaseStatsCard = () => {
  const { toast } = useToast();

  const { data: stats, isLoading, refetch } = useQuery({
    queryKey: ['database-stats'],
    queryFn: () => databaseService.getDatabaseStats(),
    refetchInterval: 30000, // Actualizar cada 30 segundos
  });

  const { data: health } = useQuery({
    queryKey: ['database-health'],
    queryFn: () => databaseService.checkDatabaseHealth(),
    refetchInterval: 60000, // Actualizar cada minuto
  });

  const handleRefresh = () => {
    refetch();
    toast({
      title: "Actualizando",
      description: "Obteniendo estadísticas más recientes de la base de datos"
    });
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent className="space-y-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (!stats) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Estadísticas de Base de Datos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
            <h3 className="font-medium text-lg mb-2">No se pudieron cargar las estadísticas</h3>
            <p className="text-muted-foreground mb-4">
              Error al conectar con la base de datos
            </p>
            <Button onClick={handleRefresh}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Reintentar
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Estadísticas de Base de Datos
          </CardTitle>
          <div className="flex items-center gap-3">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <div 
                        className={`h-3 w-3 rounded-full ${
                          health?.connectionStatus === 'healthy' 
                            ? 'bg-green-500' 
                            : health?.connectionStatus === 'error'
                            ? 'bg-red-500'
                            : 'bg-yellow-500'
                        }`}
                      />
                      <div 
                        className={`absolute -inset-1 rounded-full animate-ping opacity-30 ${
                          health?.connectionStatus === 'healthy' 
                            ? 'bg-green-400' 
                            : health?.connectionStatus === 'error'
                            ? 'bg-red-400'
                            : 'bg-yellow-400'
                        }`}
                      />
                    </div>
                    <span className={`text-sm font-medium ${
                      health?.connectionStatus === 'healthy' 
                        ? 'text-green-700' 
                        : health?.connectionStatus === 'error'
                        ? 'text-red-700'
                        : 'text-yellow-700'
                    }`}>
                      {health?.connectionStatus === 'healthy' ? 'Saludable' : 
                       health?.connectionStatus === 'error' ? 'Error' : 'Verificando...'}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-sm">
                  <div className="space-y-1">
                    <p className="font-semibold">
                      {health?.connectionStatus === 'healthy' 
                        ? '🟢 Conexión Saludable'
                        : health?.connectionStatus === 'error'
                        ? '🔴 Error de Conexión'
                        : '🟡 Verificando...'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {health?.connectionStatus === 'healthy' 
                        ? 'La base de datos está conectada y funcionando correctamente. Todas las operaciones están disponibles.'
                        : health?.connectionStatus === 'error'
                        ? 'Error de conexión con la base de datos. Revise la configuración y conectividad.'
                        : 'Verificando el estado de la conexión con la base de datos...'}
                    </p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Button variant="outline" size="sm" onClick={handleRefresh}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Información General */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <Database className="h-8 w-8 text-blue-500" />
              <div>
                <h4 className="font-medium text-blue-900">Base de Datos</h4>
                <p className="text-sm text-blue-700">{stats.general.dbName}</p>
                <p className="text-xs text-blue-600">MongoDB {stats.mongoVersion}</p>
              </div>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <BarChart3 className="h-8 w-8 text-green-500" />
              <div>
                <h4 className="font-medium text-green-900">Colecciones</h4>
                <p className="text-2xl font-bold text-green-700">{stats.general.collections}</p>
                <p className="text-xs text-green-600">{stats.general.objects.toLocaleString()} documentos</p>
              </div>
            </div>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <HardDrive className="h-8 w-8 text-purple-500" />
              <div>
                <h4 className="font-medium text-purple-900">Tamaño</h4>
                <p className="text-lg font-bold text-purple-700">
                  {databaseService.formatBytes(stats.general.dataSize)}
                </p>
                <p className="text-xs text-purple-600">
                  {databaseService.formatBytes(stats.general.storageSize)} almacenado
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Detalles por Colección */}
        <div>
          <h4 className="font-medium mb-3 flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Detalles por Colección
          </h4>
          <div className="space-y-2">
            {stats.collections.map((collection, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-blue-500" />
                  <div>
                    <p className="font-medium">{collection.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {collection.documents.toLocaleString()} docs • {collection.indexes} índices
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-mono text-sm">{databaseService.formatBytes(collection.size)}</p>
                  <p className="text-xs text-muted-foreground">
                    {databaseService.formatBytes(collection.storageSize)} almacenado
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Métricas de Rendimiento */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h5 className="font-medium mb-2">Índices</h5>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Tamaño de Índices:</span>
                <span className="text-sm font-mono">
                  {databaseService.formatBytes(stats.general.indexSize)}
                </span>
              </div>
            </div>
          </div>

          <div>
            <h5 className="font-medium mb-2">Promedios</h5>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Tamaño Promedio por Documento:</span>
                <span className="text-sm font-mono">
                  {databaseService.formatBytes(stats.general.avgObjSize)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const BackupManagementCard = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [backupType, setBackupType] = useState<'full' | 'incremental'>('full');
  const [backupDescription, setBackupDescription] = useState('');

  const { data: backups, isLoading, refetch } = useQuery({
    queryKey: ['database-backups'],
    queryFn: () => databaseService.listBackups(),
    refetchInterval: 30000,
  });

  const createBackupMutation = useMutation({
    mutationFn: (data: { type: 'full' | 'incremental'; description?: string }) =>
      databaseService.createBackup(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['database-backups'] });
      setBackupDescription('');
      toast({
        title: "Backup Iniciado",
        description: data.message,
      });
    },
    onError: (error: unknown) => {
      let errorMessage = "Error al crear backup";
      if (error && typeof error === 'object' && 'response' in error && 
          error.response && typeof error.response === 'object' && 'data' in error.response &&
          error.response.data && typeof error.response.data === 'object' && 'message' in error.response.data) {
        errorMessage = String(error.response.data.message);
      }
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    }
  });

  const deleteBackupMutation = useMutation({
    mutationFn: (backupKey: string) => databaseService.deleteBackup(backupKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['database-backups'] });
      toast({
        title: "Backup eliminado",
        description: "El backup se ha eliminado exitosamente",
      });
    },
    onError: (error: unknown) => {
      let errorMessage = "Error al eliminar backup";
      if (error && typeof error === 'object' && 'response' in error && 
          error.response && typeof error.response === 'object' && 'data' in error.response &&
          error.response.data && typeof error.response.data === 'object' && 'message' in error.response.data) {
        errorMessage = String(error.response.data.message);
      }
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    }
  });

  const restoreBackupMutation = useMutation({
    mutationFn: (backupKey: string) => databaseService.restoreBackup({ backupKey }),
    onSuccess: (data) => {
      toast({
        title: "Restauración Iniciada",
        description: data.message,
      });
    },
    onError: (error: unknown) => {
      let errorMessage = "Error al restaurar backup";
      if (error && typeof error === 'object' && 'response' in error && 
          error.response && typeof error.response === 'object' && 'data' in error.response &&
          error.response.data && typeof error.response.data === 'object' && 'message' in error.response.data) {
        errorMessage = String(error.response.data.message);
      }
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    }
  });

  const handleCreateBackup = () => {
    createBackupMutation.mutate({
      type: backupType,
      description: backupDescription || undefined
    });
  };

  const handleDeleteBackup = (backupKey: string) => {
    deleteBackupMutation.mutate(backupKey);
  };

  const handleRestoreBackup = (backupKey: string) => {
    restoreBackupMutation.mutate(backupKey);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileArchive className="h-5 w-5" />
          Gestión de Backups
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Crear Nuevo Backup */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 mb-3">Crear Nuevo Backup</h4>
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Tipo de Backup</label>
                <Select value={backupType} onValueChange={(value: 'full' | 'incremental') => setBackupType(value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full">Completo (Full)</SelectItem>
                    <SelectItem value="incremental">Incremental</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Descripción (Opcional)</label>
                <Input
                  value={backupDescription}
                  onChange={(e) => setBackupDescription(e.target.value)}
                  placeholder="Ej: Backup antes de actualización"
                />
              </div>
            </div>
            <Button 
              onClick={handleCreateBackup}
              disabled={createBackupMutation.isPending}
              className="w-full"
            >
              <Download className="h-4 w-4 mr-2" />
              {createBackupMutation.isPending ? 'Creando Backup...' : 'Crear Backup'}
            </Button>
          </div>
        </div>

        <Separator />

        {/* Lista de Backups */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium">Backups Disponibles</h4>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>

          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : backups && backups.length > 0 ? (
            <div className="space-y-2">
              {backups.map((backup, index) => (
                <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${backup.type === 'full' ? 'bg-green-500' : 'bg-blue-500'}`} />
                    <div>
                      <p className="font-medium">{backup.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {databaseService.formatDate(backup.lastModified)} • {databaseService.formatBytes(backup.size)}
                      </p>
                    </div>
                    <Badge variant={backup.type === 'full' ? 'default' : 'secondary'}>
                      {backup.type === 'full' ? 'Completo' : 'Incremental'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm">
                          <Upload className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Restaurar Backup</AlertDialogTitle>
                          <AlertDialogDescription>
                            ¿Está seguro de que desea restaurar este backup? Esto sobrescribirá todos los datos actuales de la base de datos.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleRestoreBackup(backup.key)}>
                            Restaurar
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>

                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Eliminar Backup</AlertDialogTitle>
                          <AlertDialogDescription>
                            ¿Está seguro de que desea eliminar este backup? Esta acción no se puede deshacer.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDeleteBackup(backup.key)}>
                            Eliminar
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <FileArchive className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="font-medium text-lg mb-2">No hay backups disponibles</h3>
              <p className="text-muted-foreground mb-4">
                Cree su primer backup para proteger sus datos
              </p>
            </div>
          )}
        </div>

        {/* Información de Backups Programados */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Clock className="h-5 w-5 text-green-500 mt-0.5" />
            <div>
              <h4 className="font-medium text-green-900">Backups Programados</h4>
              <p className="text-sm text-green-800 mt-1">
                • Backup diario automático a las 2:00 AM
              </p>
              <p className="text-sm text-green-800">
                • Backup semanal completo los domingos a las 3:00 AM
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export { DatabaseStatsCard, BackupManagementCard };